<template>
  <article
    class="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden relative print:shadow-none print:border-none">
    <!-- Decorative Header Bar -->
    <div class="h-2 w-full bg-gradient-to-r from-green-500 via-emerald-500 to-teal-500"></div>

    <!-- Header Section -->
    <header class="p-8 md:p-12 border-b border-gray-100 flex flex-col md:flex-row items-center gap-8">
      <img
        v-if="school?.logo"
        :src="school.logo"
        alt="School Logo"
        class="w-20 h-20 object-contain rounded-lg"
      />
      <div class="flex-1 text-center md:text-left">
        <h1 class="text-3xl font-bold text-gray-900 tracking-tight">{{ school?.name }}</h1>
        <p class="text-gray-500 mt-2 text-sm leading-relaxed max-w-lg mx-auto md:mx-0">
          {{ school?.address }}
        </p>
        <div
          class="flex flex-wrap justify-center md:justify-start gap-4 mt-4 text-xs font-medium text-gray-400 uppercase tracking-wider">
          <span v-if="school?.website" class="flex items-center gap-1"><i class="pi pi-globe"></i> {{
            school.website
            }}</span>
          <span v-if="school?.email" class="flex items-center gap-1"><i class="pi pi-envelope"></i> {{
            school.email
            }}</span>
          <span v-if="school?.mobile" class="flex items-center gap-1"><i class="pi pi-phone"></i> {{ school.mobile
            }}</span>
        </div>
      </div>
      <div class="hidden md:block text-right">
        <div
          class="bg-emerald-50 text-emerald-700 px-6 py-2 rounded-full font-bold text-sm tracking-wide border border-emerald-100 shadow-sm">
          REPORT CARD
        </div>
      </div>
    </header>

    <!--Student & Term Details-->
    <section class="bg-gray-50/50 p-8 md:p-10 grid grid-cols-1 md:grid-cols-2 gap-10">
      <div class="flex items-start gap-5">
        <div
          class="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center text-emerald-600 text-2xl font-bold">
          {{ studentName.charAt(0) }}
        </div>
        <div>
          <h2 class="text-2xl font-bold text-gray-800">{{ studentName }}</h2>
          <p class="text-gray-500 text-sm font-medium mt-1">{{ studentClass }}</p>
          <div class="mt-4 grid grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <div class="text-gray-400">Admission No</div>
            <div class="font-semibold text-gray-700">{{ leftColumnInfo['Admission Number'] }}</div>
            <div class="text-gray-400">Gender</div>
            <div class="font-semibold text-gray-700 capitalize">{{ leftColumnInfo['Gender'] }}</div>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-2 gap-6 bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
        <div>
          <div class="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-3">Academic Info</div>
          <div class="space-y-2">
            <div>
              <span class="text-gray-500 text-xs block">{{ labels.SESSION }}</span>
              <span class="text-sm font-semibold text-gray-800">{{ leftColumnInfo['Academic Session'] }}</span>
            </div>
            <div>
              <span class="text-gray-500 text-xs block">{{ labels.TERM }}</span>
              <span class="text-sm font-semibold text-gray-800">{{ leftColumnInfo['Term'] }}</span>
            </div>
          </div>
        </div>
        <div>
          <div class="text-xs text-gray-400 uppercase tracking-wider font-semibold mb-3">Attendance</div>
          <div class="space-y-1 text-sm">
            <div class="flex justify-between">
              <span class="text-gray-500">Opened</span>
              <span class="font-medium text-gray-900">{{ rightColumnInfo['No of times school opened'] }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Present</span>
              <span class="font-medium text-emerald-600">{{ rightColumnInfo['No of times present'] }}</span>
            </div>
            <div class="flex justify-between">
              <span class="text-gray-500">Absent</span>
              <span class="font-medium text-red-500">{{ rightColumnInfo['No of times absent'] }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Result Table -->
    <section class="p-8 md:p-10">
      <div class="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table class="w-full text-sm text-left">
          <thead
            class="bg-gray-50 text-gray-500 font-semibold uppercase tracking-wider text-xs border-b border-gray-200">
            <tr>
              <th v-for="header in tableHeaders" :key="header" class="px-6 py-4 whitespace-nowrap">
                {{ header }}
              </th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr v-for="(subject, idx) in subjects" :key="idx" class="hover:bg-gray-50/50 transition-colors">
              <td v-for="(header, hIdx) in tableHeaders" :key="hIdx"
                class="px-6 py-4 whitespace-nowrap text-gray-700"
                :class="{ 'font-medium text-gray-900': hIdx === 0 }">
                {{ subject[header] }}
              </td>
            </tr>
            <tr v-if="subjects.length === 0">
              <td :colspan="tableHeaders.length" class="px-6 py-12 text-center text-gray-400">
                No result data available.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- Result Summary Cards -->
    <section v-if="resultSummary && Object.keys(resultSummary).length > 0" class="px-8 pb-8 md:px-10 md:pb-10">
      <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div v-for="(item, key) in resultSummary" :key="key"
          class="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl p-4 border border-emerald-100 shadow-sm text-center">
          <div class="text-xs font-semibold text-emerald-600 uppercase tracking-wider mb-1">{{ item.label }}</div>
          <div class="text-2xl font-bold text-emerald-900">
            {{ typeof item.value === 'number' ? item.value.toFixed(1) : item.value }}
            <span v-if="String(key).includes('percentage') || String(key).includes('percent')" class="text-lg">%</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Remarks & Grading -->
    <section class="grid grid-cols-1 lg:grid-cols-3 gap-8 p-8 md:p-10 border-t border-gray-100 bg-gray-50/30">
      <!-- Grade Key -->
      <div>
        <h3 class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-4">Grading System</h3>
        <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div v-for="grade in grades" :key="grade.letter"
            class="flex border-b border-gray-50 last:border-none p-3 text-sm">
            <div class="w-12 font-bold text-center text-emerald-600 bg-emerald-50 rounded-md py-1">{{ grade.letter }}</div>
            <div class="flex-1 text-right text-gray-600 self-center">{{ grade.range }}</div>
          </div>
        </div>
      </div>

      <!-- Comments -->
      <div class="lg:col-span-2 space-y-6">
        <!-- Dynamic Feedback Categories (Tables) -->
        <div v-for="(groupFeedbacks, categoryName) in categorizedFeedbacks.groups" :key="categoryName" class="space-y-3">
          <h3 class="text-sm font-bold text-primary uppercase tracking-wide">[ {{ categoryName }} ]</h3>
          <div class="bg-white rounded-lg shadow-sm border border-gray-300 overflow-hidden">
            <table class="w-full border-collapse">
              <tbody>
                <tr v-for="(fb, index) in groupFeedbacks" :key="fb.id"
                  :class="index !== groupFeedbacks.length - 1 ? 'border-b border-gray-300' : ''">
                  <td class="p-3 text-sm font-semibold text-gray-700 border-r border-gray-300 align-top w-1/4">
                    {{ fb.label }}
                  </td>
                  <td class="p-3">
                    <FeedbackRatingInput v-if="isRatingItem(fb)" v-model="fb.rating" :feedback="fb" />
                    <Textarea v-else v-model="fb.value" autoResize rows="2"
                      class="w-full text-sm text-gray-700 p-2 border border-gray-200 rounded focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="Enter remark..." />
                    <div class="flex justify-between items-center mt-2">
                       <span class="text-xs text-gray-400" v-if="fb.updated_at">
                        <i class="pi pi-clock mr-1"></i> {{ new Date(fb.updated_at).toLocaleString() }}
                      </span>
                      <span v-else class="text-xs text-gray-400">Not saved</span>
                      <Button label="Save" icon="pi pi-check" size="small"
                        @click="$emit('save-feedback', fb.feedback_id, fb.id, fb.value, fb.rating)"
                        data-testid="btn-results-save-feedback-group"
                        class="p-button-sm !py-1 !px-2 !text-xs p-button-primary" />
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- General Remarks (Cards) -->
        <div v-if="categorizedFeedbacks.general.length" class="space-y-3">
          <h3 class="text-sm font-bold text-gray-600 uppercase tracking-wide">General Remarks</h3>
          <div v-for="(fb, i) in categorizedFeedbacks.general" :key="fb.id"
            class="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 relative group">
            <div class="absolute top-0 left-0 w-1 p-0.5 h-full rounded-l-2xl"
              :class="Number(i) % 2 === 0 ? 'bg-indigo-500' : 'bg-teal-500'"></div>
            <h4 class="text-sm font-bold uppercase tracking-wide mb-2"
              :class="Number(i) % 2 === 0 ? 'text-indigo-900' : 'text-teal-900'">{{ fb.label }}</h4>

            <FeedbackRatingInput v-if="isRatingItem(fb)" v-model="fb.rating" :feedback="fb" />
                    <Textarea v-else v-model="fb.value" autoResize rows="3"
              class="w-full text-sm text-gray-700 p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              placeholder="Enter remark here..." />

            <div class="flex justify-between items-center mt-3">
              <span class="text-xs text-gray-400" v-if="fb.updated_at">
                <i class="pi pi-clock mr-1"></i> Saved: {{ new Date(fb.updated_at).toLocaleString() }}
              </span>
              <span v-else class="text-xs text-gray-400">Not saved yet</span>

              <Button label="Save" icon="pi pi-check" size="small"
                @click="$emit('save-feedback', fb.feedback_id, fb.id, fb.value, fb.rating)"
                data-testid="btn-results-save-feedback-general"
                :class="Number(i) % 2 === 0 ? 'p-button-indigo' : 'p-button-teal'"
                class="p-button-sm !py-1 !px-3 !text-xs" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Next Term -->
    <footer class="bg-emerald-900 text-white p-8 text-center">
      <p class="text-emerald-200 text-xs font-bold uppercase tracking-widest mb-1">Important</p>
      <h3 class="text-lg md:text-xl font-medium">Next Term Resumes: <span class="font-bold text-white">{{ nextTermDate }}</span></h3>
    </footer>
  </article>
</template>

<script setup lang="ts">
import FeedbackRatingInput from './FeedbackRatingInput.vue';
import { isRatingItem } from '@/utils/resultFeedbackRating';
import type { School, StudentResultSummary, Grade } from '@/types/composition-functions';
import { useSchoolLabels } from '@/composables/useSchoolLabels';

const { labels } = useSchoolLabels();

const props = defineProps<{
  school: School;
  studentName: string;
  studentClass: string;
  leftColumnInfo: Record<string, string | number>;
  rightColumnInfo: Record<string, string | number>;
  tableHeaders: string[];
  subjects: Record<string, unknown>[];
  resultSummary: StudentResultSummary;
  grades: Grade[];
  categorizedFeedbacks: { groups: Record<string, Record<string, unknown>[]>; general: Record<string, unknown>[] };
  nextTermDate: string;
}>();

defineEmits(['save-feedback']);
</script>
